// The conversation state: what Postgres has (ai_conversation) plus the turn in flight, streamed from the assistant service.
import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Json } from '@/lib/api';
import { streamTurn, type TurnEvent } from '@/lib/assistant';
import { errorText } from '@/lib/errors';
import { uploadBlob } from '@/components/Attachments';

export type ThreadItem =
  | { kind: 'message'; key: string; role: 'user' | 'assistant'; text: string; at: string; streaming?: boolean; model?: string | null; attachments?: Json[]; cost?: number | null }
  | { kind: 'proposal'; key: string; at: string; proposal: Json }
  | { kind: 'matches'; key: string; at: string; strong: boolean; candidates: Json[] }
  | { kind: 'note'; key: string; at: string; tone: 'info' | 'warn' | 'danger'; text: string };

export type PendingFile = { file: File; blob: Blob; name: string; cropped?: boolean; url: string };

const stamp = (offset = 0) => new Date(Date.now() + offset).toISOString();
const failureText = (code: string, detail?: string | null) => errorText(new ApiError(code, detail ?? undefined));

export function useAssistant(conversationId: string | null, opts: { context?: Json | null; onConversation?: (id: string) => void } = {}) {
  const qc = useQueryClient();
  const convQ = useQuery({ queryKey: ['ai-conv', conversationId], queryFn: () => api.aiConversation(conversationId!), enabled: !!conversationId, staleTime: 5_000 });
  const [live, setLive] = useState<ThreadItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const extras = useRef<ThreadItem[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  // while a turn runs, the stored messages after this id belong to it and are shown live instead, so nothing appears twice
  const baseline = useRef<number | null>(null);
  const conv = convQ.data as Json | undefined;
  const convRef = useRef(conv); convRef.current = conv;

  const items = useMemo<ThreadItem[]>(() => {
    const byId = new Map<string, Json>(((conv?.attachments ?? []) as Json[]).map((a) => [a.id as string, a]));
    const msgs = (conv?.messages ?? []) as Json[];
    const cut = busy ? baseline.current : null;
    const out: ThreadItem[] = [];
    for (const m of msgs) {
      if (cut != null && Number(m.id) > cut) continue;
      const blocks = (m.content ?? []) as Json[];
      const failed = blocks.find((b) => b.type === 'error');
      if (failed) { out.push({ kind: 'note', key: `m${m.id}`, at: m.created_at, tone: failed.code === 'turn_stopped' ? 'warn' : 'danger', text: failureText(String(failed.code), failed.detail as string | null) }); continue; }
      const text = blocks.filter((b) => b.type === 'text').map((b) => String(b.text ?? '')).join('\n');
      if (m.role === 'user' || text) out.push({ kind: 'message', key: `m${m.id}`, role: m.role, text: m.purged ? '(removed by retention)' : text, at: m.created_at, model: m.model, cost: m.cost_inr, attachments: ((m.attachment_ids ?? []) as string[]).map((id) => byId.get(id)).filter(Boolean) as Json[] });
    }
    // a question with nothing stored after it, and no turn running here: the turn died before it could answer or record why
    const last = msgs[msgs.length - 1];
    if (!busy && last?.role === 'user' && !last.purged) out.push({ kind: 'note', key: `unanswered-${last.id}`, at: `${last.created_at}~`, tone: 'warn', text: 'No answer was saved for this message. Send it again if you still need one.' });
    for (const p of (conv?.proposals ?? []) as Json[]) out.push({ kind: 'proposal', key: `p${p.id}`, at: p.created_at, proposal: p });
    const seen = new Set(out.map((x) => x.key));
    // the stop the service recorded replaces the note made here when Stop was pressed
    const stoppedAfter = (id: number) => msgs.some((m) => Number(m.id) > id && ((m.content ?? []) as Json[]).some((b) => b.type === 'error' && b.code === 'turn_stopped'));
    for (const x of [...extras.current, ...live]) {
      if (seen.has(x.key) || (x.key.startsWith('stopped-') && stoppedAfter(Number(x.key.slice(8))))) continue;
      out.push(x); seen.add(x.key);
    }
    return out.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
  }, [conv, live, busy]);

  const send = useCallback(async (text: string, files: PendingFile[], thinkHarder = false) => {
    if (busy) return;
    baseline.current = Math.max(0, ...((convRef.current?.messages ?? []) as Json[]).map((m) => Number(m.id)));
    setBusy(true); setStatus('Starting…');
    const ac = new AbortController(); abortRef.current = ac;
    let cid = conversationId;
    const note = (tone: 'info' | 'warn' | 'danger', t: string, key?: string) => { const item: ThreadItem = { kind: 'note', key: key ?? `n-${Date.now()}-${Math.random()}`, at: stamp(), tone, text: t }; extras.current.push(item); setLive((xs) => [...xs, item]); };
    try {
      const s = await api.aiOpenSession(cid, opts.context ?? null);
      cid = s.conversation_id;
      // the panel stays mounted when its own first message creates the conversation (the page only updates its address)
      if (!conversationId) opts.onConversation?.(cid);
      const uploaded: { id: string; blob: Blob; name: string; row: Json }[] = [];
      for (const f of files) {
        setStatus(`Uploading ${f.name}…`);
        const kind = f.blob.type === 'application/pdf' || /\.pdf$/i.test(f.name) ? 'vendor_bill' : 'receipt_photo';
        const row = await uploadBlob('ai_conversation', cid, kind, f.blob, f.name);
        uploaded.push({ id: row.id, blob: f.blob, name: f.name, row });
      }
      setStatus(uploaded.length ? 'Reading the document…' : 'Thinking…');
      const userKey = `live-u-${Date.now()}`, botKey = `live-a-${Date.now()}`;
      setLive([{ kind: 'message', key: userKey, role: 'user', text, at: stamp(), attachments: uploaded.map((u) => u.row) }, { kind: 'message', key: botKey, role: 'assistant', text: '', at: stamp(1), streaming: true }]);
      const append = (delta: string) => setLive((xs) => xs.map((x) => (x.key === botKey && x.kind === 'message' ? { ...x, text: x.text + delta } : x)));
      await streamTurn({ token: s.token, text, files: uploaded, attachmentIds: uploaded.map((u) => u.id), thinkHarder, signal: ac.signal, onEvent: async (e: TurnEvent) => {
        if (e.type === 'status') setStatus(e.text);
        else if (e.type === 'text') { setStatus(null); append(e.delta); }
        else if (e.type === 'matches') { const item: ThreadItem = { kind: 'matches', key: `x-${Date.now()}-${Math.random()}`, at: stamp(), strong: e.strong, candidates: e.candidates }; extras.current.push(item); setLive((xs) => [...xs, item]); }
        else if (e.type === 'proposal') { try { const p = await api.aiProposal(e.id); setLive((xs) => [...xs.filter((x) => x.key !== `p${p.id}`), { kind: 'proposal', key: `p${p.id}`, at: stamp(), proposal: p }]); } catch { /* the register still shows it */ } }
        else if (e.type === 'escalated') note('info', `Switched to ${e.model}: ${e.reason}.`);
        // keyed like the stored failure, so after the reload it shows once
        else if (e.type === 'error') { setStatus(null); note(e.code === 'turn_stopped' ? 'warn' : 'danger', failureText(e.code, e.detail), e.message_id ? `m${e.message_id}` : undefined); }
        else if (e.type === 'done') { setStatus(null); setLive((xs) => xs.map((x) => (x.key === botKey && x.kind === 'message' ? { ...x, streaming: false, model: e.model, cost: e.cost_inr } : x))); }
      } });
      // the stream ends silently when the person stops it; the stored stop, once fetched, replaces this note
      if (ac.signal.aborted) note('warn', failureText('turn_stopped'), `stopped-${baseline.current ?? 0}`);
    } catch (e) {
      note('danger', errorText(e));
    } finally {
      abortRef.current = null;
      if (cid) { await qc.invalidateQueries({ queryKey: ['ai-conv', cid] }); await qc.refetchQueries({ queryKey: ['ai-conv', cid] }).catch(() => {}); }
      qc.invalidateQueries({ queryKey: ['ai-convs'] });
      setLive((xs) => xs.filter((x) => x.kind === 'note' || x.kind === 'matches'));
      setBusy(false); setStatus(null);
    }
  }, [busy, conversationId, opts, qc]);

  const stop = useCallback(() => abortRef.current?.abort(), []);
  const refresh = useCallback(() => (conversationId ? qc.invalidateQueries({ queryKey: ['ai-conv', conversationId] }) : Promise.resolve()), [conversationId, qc]);
  return { items, send, stop, busy, status, conversation: conv ?? null, loading: convQ.isLoading, error: convQ.error, refresh };
}
